import { Box, Tabs } from '@factorialco/deprecated-design-system'
import ReactPDF from '@react-pdf/renderer'
import { fetchEnvironment } from 'config/environment'
import {
  FilterType,
  buildProcurementPurchaseOrdersDatatableFilter,
  useProcurementPurchaseOrdersDatatable,
} from 'generated/resources/datatable.graphql'
import { ContactFragment, PrintablePurchaseOrderDocument } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import useSearchParams from 'modules/core/hooks/useSearchParams'
import { buildClient } from 'modules/core/lib/graphql/buildClient'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmptyState } from 'modules/procurement/components/purchaseOrders/EmptyState'
import PrintablePurchaseOrder from 'modules/procurement/components/purchaseOrders/PrintablePurchaseOrder'
import { PurchaseOrdersInsights } from 'modules/procurement/components/purchaseOrders/PurchaseOrdersInsights'
import type { PurchaseOrdersTab } from 'modules/procurement/pages/PurchaseOrdersPage/types'

import { PurchaseOrderProjections } from './projections'

interface Props {
  onTabChange: (tab: PurchaseOrdersTab) => void
  currentTab: PurchaseOrdersTab
  vendor?: ContactFragment
}

const PurchaseOrdersDataTable = ({ onTabChange, currentTab, vendor }: Props) => {
  const {
    company,
    access: { locale },
  } = useCurrentSession()
  const { status: statusSearchParamValue } = useSearchParams<{
    status?: string
  }>()

  const { dateFrom, dateTo } = useSearchParams<{
    dateFrom?: string
    dateTo?: string
  }>()

  const { debouncedSearch, search, onSearch } = useDebouncedSearch()

  const handleDownloadPdf = async (id: number) => {
    const environment = await fetchEnvironment()
    const graphQLClient = await buildClient(environment)

    const { data, error } = await graphQLClient.query({
      query: PrintablePurchaseOrderDocument,
      variables: { id, companyId: company.id },
    })

    const purchaseOrder = data.procurement.purchaseOrdersConnection.nodes[0]

    if (error || !purchaseOrder) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('procurement.purchase_orders.pdf.fetch_error'),
      })
      return
    }

    const pdf = ReactPDF.pdf(
      <PrintablePurchaseOrder
        locale={locale}
        purchaseOrder={purchaseOrder}
        logo={company.logo ?? null}
      />
    )
    const blob = await pdf.toBlob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')

    a.href = url
    a.download = purchaseOrder.formattedPoNumber
    a.click()
  }

  const tabs: { id: PurchaseOrdersTab; label: string }[] = [
    {
      id: 'all',
      label: i18n.t('procurement.purchase_orders.tabs.all'),
    },
    {
      id: 'draft',
      label: i18n.t('procurement.purchase_orders.status.draft'),
    },
    {
      id: 'pending',
      label: i18n.t('procurement.purchase_orders.status.pending'),
    },
    {
      id: 'ordered',
      label: i18n.t('procurement.purchase_orders.status.ordered'),
    },
    {
      id: 'partial',
      label: i18n.t('procurement.purchase_orders.status.partial'),
    },
    {
      id: 'received',
      label: i18n.t('procurement.purchase_orders.status.received'),
    },
    {
      id: 'closed',
      label: i18n.t('procurement.purchase_orders.status.closed'),
    },
  ]

  const source = useProcurementPurchaseOrdersDatatable(
    PurchaseOrderProjections({
      locale,
      skipVendor: !!vendor,
      handleDownloadPdf,
    }),
    {
      variables: {
        companyId: company.id,
        status: currentTab == 'all' ? undefined : currentTab,
        search: debouncedSearch,
        vendorId: vendor?.id,
      },
    }
  )

  const statusFilterValue = statusSearchParamValue?.split(',')

  const statusFilter = buildProcurementPurchaseOrdersDatatableFilter(source, {
    identifier: 'status',
    title: i18n.t('procurement.purchase_orders.list.status'),
    path: 'status',
    default: !!statusFilterValue,
    type: FilterType.String,
    categories: [
      {
        kind: 'MultipleChoice',
        operation: 'in',
        value: statusFilterValue,
        options: [
          {
            label: i18n.t('procurement.purchase_orders.status.draft'),
            value: 'draft',
          },
          {
            label: i18n.t('procurement.purchase_orders.status.pending'),
            value: 'pending',
          },
          {
            label: i18n.t('procurement.purchase_orders.status.ordered'),
            value: 'ordered',
          },
          {
            label: i18n.t('procurement.purchase_orders.status.partial'),
            value: 'partial',
          },
          {
            label: i18n.t('procurement.purchase_orders.status.received'),
            value: 'received',
          },
          {
            label: i18n.t('procurement.purchase_orders.status.closed'),
            value: 'closed',
          },
        ],
      },
    ],
  })

  const dateFilter = buildProcurementPurchaseOrdersDatatableFilter(source, {
    identifier: 'date',
    title: i18n.t('procurement.purchase_orders.list.date'),
    path: 'date',
    default: !!dateFrom && !!dateTo,
    type: FilterType.Date,
    categories: [
      {
        kind: 'DatePicker',
        operation: 'between',
        value: !!dateFrom && !!dateTo ? [dateFrom, dateTo] : undefined,
      },
    ],
  })

  const filters = currentTab == 'all' ? [statusFilter, dateFilter] : [dateFilter]

  const currency = company.legalEntities.find((el) => el.main)?.currency ?? 'EUR'
  const companyId = company.id

  return (
    <Box gap="s16">
      {!vendor && (
        <Tabs
          currentId={currentTab}
          onTabClicked={(tab: PurchaseOrdersTab) => {
            onTabChange(tab)
          }}
        >
          {tabs.map((tab) => (
            <Tabs.Tab key={tab.id} id={tab.id} label={tab.label} />
          ))}
        </Tabs>
      )}
      <DataTable.Provider
        source={source}
        persistFilters={false}
        pagination={{
          kind: 'offset',
          itemsPerPage: 25,
        }}
        filters={filters}
        sort={{
          path: 'description',
          direction: 'asc',
        }}
      >
        <Box gap="s16">
          <DataTable.HeadBar
            exportFileName="purchase-orders"
            search={{
              placeholder: i18n.t('common.search'),
              value: search,
              onChange: onSearch,
            }}
          />
          {companyId && currency && !vendor && (
            <PurchaseOrdersInsights
              locale={locale}
              currency={currency}
              companyId={companyId}
              search={search}
            />
          )}
          <DataTable.Table
            autoWidth
            emptyState={
              <EmptyState
                description={i18n.t('procurement.purchase_orders.empty.description_company')}
              />
            }
          />
        </Box>
      </DataTable.Provider>
    </Box>
  )
}

export default PurchaseOrdersDataTable
